import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    detectBrowserTimezone,
    supportedTimezones,
    timezoneLabel,
} from "../../domain/formatting";

interface TimezonePickerProps {
    value: string;
    onChange: (timezone: string) => void;
    disabled?: boolean;
    id?: string;
}

/**
 * IANA timezone selector.
 *
 * The browser's zone only seeds the *suggestion*; the stored value always wins,
 * because the therapist may work in a zone other than the one they travel in.
 */
export default function TimezonePicker({
    value,
    onChange,
    disabled = false,
    id = "scheduler-timezone",
}: TimezonePickerProps) {
    const { t } = useTranslation();

    const zones = useMemo(() => supportedTimezones(), []);
    const suggested = useMemo(() => detectBrowserTimezone(), []);

    // A stored zone the runtime does not enumerate must still be selectable,
    // otherwise opening the page would silently change the therapist's setting.
    const options = useMemo(
        () => (zones.includes(value) ? zones : [value, ...zones]),
        [zones, value]
    );

    return (
        <div className="flex flex-col gap-2">
            <label
                htmlFor={id}
                className="text-sm font-medium text-content-secondary"
            >
                {t("scheduler.settings.timezone")}
            </label>
            <select
                id={id}
                value={value}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
            >
                {options.map((zone) => (
                    <option key={zone} value={zone}>
                        {timezoneLabel(zone)}
                    </option>
                ))}
            </select>
            {value !== suggested && (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(suggested)}
                    className="self-start text-xs text-brand-600 underline"
                >
                    {t("scheduler.settings.useBrowserTimezone", {
                        timezone: suggested,
                    })}
                </button>
            )}
        </div>
    );
}
